"use client";
import { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import Player from "video.js/dist/types/player";
import Hls from "hls.js";
import "video.js/dist/video-js.css";

export const VideoPlayer = ({
  videoLink,
  type = "video/mp4",
}: {
  videoLink: string;
  type?: string;
}) => {
  const videoElemRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<Player | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isReady) return;

    const videoElement = videoElemRef.current;
    if (!videoElement) return;

    // 1. Clean up any existing Video.js and Hls.js instances first
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }

    // 2. Initialize Video.js ONLY as a UI skin wrapper (no sources passed here)
    const player = videojs(videoElement, {
      controls: true,
      autoplay: true,
      preload: "auto",
      responsive: true,
      fluid: true,
    });
    playerRef.current = player;

    // 3. Handle the HLS streaming logic manually via hls.js or Native Safari
    const isHls = type === "application/x-mpegURL" || videoLink.includes(".m3u8");

    if (isHls) {
      // Native Safari support
      if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
        videoElement.src = videoLink;
      } 
      // Chrome / Firefox / Edge via hls.js
      else if (Hls.isSupported()) {
        const hls = new Hls({
          // Force hls.js to completely bypass the broken browser cache matching
          xhrSetup: (xhr) => {
            xhr.withCredentials = false;
          }
        });
        
        hls.loadSource(videoLink);
        hls.attachMedia(videoElement);
        hlsRef.current = hls;

        hls.on(Hls.Events.ERROR, function (event, data) {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                break;
            }
          }
        });
      }
    } else {
      // Fallback for standard progressive MP4 files
      player.src({ src: videoLink, type });
    }

    // Cleanup on unmount or URL change
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [videoLink, type, isReady]);

  return (
    <div data-vjs-player>
      <video
        ref={videoElemRef}
        className="video-js vjs-default-skin vjs-big-play-centered"
        controls
        loop
        playsInline
      />
    </div>
  );
};